/**
 * @file LocalStorage 工具函式
 * @description 封裝 localStorage 操作，支援資料過期機制與 JSON 序列化
 */
import ExceptionHandleService from 'utils/exceptionHandler';

const _EHS = new ExceptionHandleService({
  _NAME: 'utils/localStorageUtil.ts',
  _NOTICE: ''
});

/** localStorage key 前綴 */
const PRE_TEXT = 'personal_';

/** 記憶體快取，避免重複讀取 localStorage + JSON.parse */
const _cache = new Map<string, { value: unknown; expiry: number }>();

/** localStorage 儲存項目介面 (含過期時間) */
interface StorageItem<T> {
  value: T;
  expiry: number;
}

/**
 * set key value in local storage.
 */
const setItem = <T>(
  key: string,
  value: T,
  ttl = 60 * 60 * 1000 // 1hr
): void => {
  try {
    const now = new Date();
    const expiry = now.getTime() + ttl;
    const item: StorageItem<T> = { value, expiry };
    localStorage.setItem(PRE_TEXT + key, JSON.stringify(item));
    _cache.set(key, { value, expiry });
  } catch (error) {
    console.log('[ERROR] setItem :');
    console.log(error);
    _EHS.errorReport(error, 'setItem', _EHS._LEVEL.ERROR);
  }
};

/**
 * get value from local storage, if null return null.
 */
const getItem = <T = unknown>(key: string): T | null => {
  try {
    const now = Date.now();

    // 優先從記憶體快取讀取
    const cached = _cache.get(key);
    if (cached) {
      if (now > cached.expiry) {
        _cache.delete(key);
        localStorage.removeItem(PRE_TEXT + key);
        return null;
      }
      return cached.value as T;
    }

    // 快取未命中，從 localStorage 讀取
    const itemStr = localStorage.getItem(PRE_TEXT + key);
    if (!itemStr) {
      return null;
    }

    const item: StorageItem<T> = JSON.parse(itemStr);
    if (now > item.expiry) {
      localStorage.removeItem(PRE_TEXT + key);
      return null;
    }

    // 寫入快取
    _cache.set(key, { value: item.value, expiry: item.expiry });
    return item.value;
  } catch (error) {
    console.log(`${key} 取得參數失敗`);
    _EHS.errorReport(error, 'getItem', _EHS._LEVEL.ERROR);
    return null;
  }
};

/**
 * remove.
 */
const removeItem = (key: string): void => {
  try {
    _cache.delete(key);
    localStorage.removeItem(PRE_TEXT + key);
  } catch (error) {
    _EHS.errorReport(error, 'removeItem', _EHS._LEVEL.ERROR);
  }
};

/**
 * 清除所有 localStorage 資料
 */
const cleanAll = (): void => {
  try {
    _cache.clear();
    localStorage.clear();
  } catch (error) {
    _EHS.errorReport(error, 'cleanAll', _EHS._LEVEL.ERROR);
  }
};

export default { setItem, getItem, removeItem, cleanAll };
